import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Demand } from '../types';
import { demandsAPI, inboxStatsAPI, attachmentsAPI } from '../api';
import {
  Check, RefreshCw, Star, Archive, Edit3, FolderOpen,
  AlertTriangle, MessageSquare, Mail, FileText, User, MapPin,
  Clock, Sparkles, Phone, Eye, X, Download, Loader2,
  Activity, CheckCircle2, TriangleAlert, Radio, Search
} from 'lucide-react';
import { MobileDemandCard } from './mobile/MobileDemandCard';
import { useAlert } from '../contexts/AlertContext';

interface InboxProps {
  demands: Demand[];
  globalSearch: string;
  onRefresh: () => void;
  onSelectDemand: (id: string) => void;
  onSelectDemandEdit: (id: string) => void;
  userRole: string;
  userPermissions: string[];
}

interface QueueStats {
  recebidas: number;
  processando: number;
  revisaoObrigatoria: number;
  resolvidasHoje: number;
  total: number;
}

// ─────────────────────────────────────────────────────────────
// Sub-componentes visuais
// ─────────────────────────────────────────────────────────────

const ConfidenceBar: React.FC<{ score: number }> = ({ score }) => {
  const color = score >= 85 ? '#10b981' : score >= 70 ? '#f59e0b' : '#ef4444';
  const label = score >= 85 ? 'Alta' : score >= 70 ? 'Média' : 'Baixa';
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-20 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{ width: `${score}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] font-bold tabular-nums" style={{ color }}>
        {score}% <span className="font-normal opacity-70">{label}</span>
      </span>
    </div>
  );
};

const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  if (source === 'WHATSAPP') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-200/50">
      <MessageSquare className="w-2.5 h-2.5" /> WhatsApp
    </span>
  );
  if (source === 'EMAIL') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400 border border-blue-200/50">
      <Mail className="w-2.5 h-2.5" /> E-mail
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200/50">
      <FileText className="w-2.5 h-2.5" /> Manual
    </span>
  );
};

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const styles: Record<string, string> = {
    URGENTE: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-400 animate-pulse',
    ALTA: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-400',
    MEDIA: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-400',
    BAIXA: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[9px] font-bold border ${styles[priority] || styles.BAIXA}`}>
      {priority}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// Modal: Visualizador de Documento lado a lado com dados da IA
// ─────────────────────────────────────────────────────────────

interface DocumentViewerProps {
  demand: Demand;
  onClose: () => void;
  onApprove: () => void;
  onArchive: () => void;
  onOpenFull: () => void;
  loading: boolean;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  demand, onClose, onApprove, onArchive, onOpenFull, loading
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [docError, setDocError] = useState(false);
  const attachment = demand.attachments?.[0];
  const isImage = attachment?.fileType?.includes('image') ||
    !!attachment?.fileName?.match(/\.(jpg|jpeg|png)$/i);

  useEffect(() => {
    if (!attachment?.fileUrl) return;
    setLoadingDoc(true);
    setDocError(false);
    let objectUrl: string | null = null;

    attachmentsAPI.fetchBlob(attachment.fileUrl)
      .then(result => {
        if (result) {
          objectUrl = URL.createObjectURL(result.blob);
          setBlobUrl(objectUrl);
        } else {
          setDocError(true);
        }
      })
      .catch(() => setDocError(true))
      .finally(() => setLoadingDoc(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.fileUrl]);

  const formatValue = (v: number | null | undefined) => {
    if (!v) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-6xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-950/50 rounded-lg">
              <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400 font-mono">{demand.protocol}</p>
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">{demand.title}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 hover:text-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body: Duas Colunas */}
        <div className="flex-1 flex overflow-hidden min-h-0">

          {/* Esquerda: Visualizador de Documento */}
          <div className="w-3/5 border-r border-slate-100 dark:border-slate-800 flex flex-col">
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
              <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Documento Original
                {attachment && <span className="text-slate-400 font-normal">— {attachment.fileName}</span>}
              </span>
              {blobUrl && (
                <a
                  href={blobUrl}
                  download={attachment?.fileName}
                  className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Download className="w-3 h-3" /> Baixar
                </a>
              )}
            </div>

            <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-950 overflow-hidden">
              {loadingDoc ? (
                <div className="flex flex-col items-center gap-3 text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <p className="text-sm">Carregando documento...</p>
                </div>
              ) : docError ? (
                <div className="flex flex-col items-center gap-3 text-slate-400 p-8 text-center">
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                  <p className="text-sm font-medium">Documento não disponível</p>
                  <p className="text-xs text-slate-400">O arquivo pode não ter sido salvo neste servidor.</p>
                </div>
              ) : !attachment ? (
                <div className="flex flex-col items-center gap-3 text-slate-400 p-8 text-center">
                  <FileText className="w-8 h-8 opacity-30" />
                  <p className="text-sm">Nenhum documento anexado</p>
                  <p className="text-xs opacity-60">Esta demanda foi criada por texto ou manualmente.</p>
                </div>
              ) : blobUrl ? (
                isImage ? (
                  <img src={blobUrl} alt={attachment.fileName} className="max-w-full max-h-full object-contain" />
                ) : (
                  <iframe src={blobUrl} title="Documento" className="w-full h-full border-0" />
                )
              ) : null}
            </div>
          </div>

          {/* Direita: Dados Extraídos pela IA */}
          <div className="w-2/5 flex flex-col overflow-y-auto">
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                Dados Extraídos pela IA
              </span>
            </div>

            <div className="flex-1 p-4 space-y-4 overflow-y-auto">
              {/* Score de Confiança */}
              <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <p className="text-[10px] text-slate-400 font-semibold mb-1.5 uppercase tracking-wider">Score de Confiança IA</p>
                <div className="flex items-center gap-3">
                  <div className="relative w-14 h-14 shrink-0">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                      <circle
                        cx="18" cy="18" r="15.9" fill="none"
                        stroke={demand.confidenceScore >= 85 ? '#10b981' : demand.confidenceScore >= 70 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="3"
                        strokeDasharray={`${demand.confidenceScore} ${100 - demand.confidenceScore}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[11px] font-black" style={{
                        color: demand.confidenceScore >= 85 ? '#10b981' : demand.confidenceScore >= 70 ? '#f59e0b' : '#ef4444'
                      }}>
                        {demand.confidenceScore}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      {demand.confidenceScore >= 85 ? '✅ Alta Confiança' : demand.confidenceScore >= 70 ? '⚠️ Revisão Recomendada' : '🔴 Revisão Obrigatória'}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {demand.aiSource === 'openai' ? 'Processado por GPT-4o' : 'Análise automática'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Campos Extraídos */}
              {[
                { label: 'Assunto', value: demand.title, icon: '📋' },
                { label: 'Resumo', value: demand.summary, icon: '📝', multiline: true },
                { label: 'Solicitante', value: demand.solicitanteName, icon: '👤' },
                { label: 'Cidade / UF', value: demand.city ? `${demand.city} - ${demand.uf || 'SP'}` : null, icon: '📍' },
                { label: 'Área Temática', value: demand.themeArea, icon: '🏷️' },
                { label: 'Esfera', value: demand.sphere, icon: '🏛️' },
                { label: 'Gabinete', value: demand.cabinet === 'STATE' ? 'Dani Alonso (Estadual)' : demand.cabinet === 'FEDERAL' ? 'Capitão Augusto (Federal)' : 'Ambos os Gabinetes', icon: '⚖️' },
                { label: 'Valor Solicitado', value: formatValue(demand.valueRequested), icon: '💰' },
                { label: 'Telefone', value: demand.contact?.phone, icon: '📞' },
                { label: 'Encaminhamento', value: demand.forwardingSuggestion, icon: '➡️', multiline: true },
              ].map(({ label, value, icon, multiline }) => value ? (
                <div key={label} className="space-y-0.5">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{icon} {label}</p>
                  <p className={`text-xs text-slate-700 dark:text-slate-200 ${multiline ? 'leading-relaxed' : 'font-medium'}`}>
                    {value}
                  </p>
                </div>
              ) : null)}
            </div>

            {/* Ações */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-2 shrink-0">
              <button
                onClick={onApprove}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-emerald-500/20"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Aprovar — Mover para Em Análise
              </button>
              <div className="flex gap-2">
                <button
                  onClick={onOpenFull}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 text-blue-700 dark:text-blue-400 border border-blue-200/50 rounded-xl text-xs font-semibold transition-all"
                >
                  <FolderOpen className="w-3.5 h-3.5" /> Abrir Completo
                </button>
                <button
                  onClick={onArchive}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-500 border border-slate-200/50 rounded-xl text-xs font-semibold transition-all disabled:opacity-60"
                >
                  <Archive className="w-3.5 h-3.5" /> Arquivar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Componente Principal: Central de Triagem
// ─────────────────────────────────────────────────────────────

export const Inbox: React.FC<InboxProps> = ({
  demands,
  globalSearch,
  onRefresh,
  onSelectDemand,
  onSelectDemandEdit,
  userPermissions,
}) => {
  const { showAlert } = useAlert();
  const [stats, setStats] = useState<QueueStats>({
    recebidas: 0, processando: 0, revisaoObrigatoria: 0, resolvidasHoje: 0, total: 0
  });
  const [isLive, setIsLive] = useState(true);
  const [filterSource, setFilterSource] = useState<'all' | 'WHATSAPP' | 'EMAIL' | 'MANUAL'>('all');
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [newDemandIds, setNewDemandIds] = useState<Set<string>>(new Set());
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  const [viewerDemand, setViewerDemand] = useState<Demand | null>(null);
  const prevIdsRef = useRef<Set<string>>(new Set());

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSource, filterFavorite]);

  // ── Fetch stats ──────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const data = await inboxStatsAPI.stats();
      setStats(data);
    } catch (_) {}
  }, []);

  // ── Auto-refresh polling (15s, escalonado) ──────────────
  useEffect(() => {
    if (!isLive) return;

    fetchStats();
    onRefresh();

    const statsInterval   = setInterval(fetchStats, 15000);
    const demandsInterval = setInterval(onRefresh, 17000);

    return () => {
      clearInterval(statsInterval);
      clearInterval(demandsInterval);
    };
  }, [isLive, fetchStats]);

  // ── Detectar novas demandas ──────────────────────────────
  useEffect(() => {
    const currentIds = new Set(demands.map(d => d.id));
    const arrived    = new Set<string>();

    currentIds.forEach(id => {
      if (prevIdsRef.current.size > 0 && !prevIdsRef.current.has(id)) arrived.add(id);
    });

    if (arrived.size > 0) {
      setNewDemandIds(prev => new Set([...prev, ...arrived]));
      setTimeout(() => {
        setNewDemandIds(prev => {
          const next = new Set(prev);
          arrived.forEach(id => next.delete(id));
          return next;
        });
      }, 12000);
    }

    prevIdsRef.current = currentIds;
  }, [demands]);

  // ── Ações ────────────────────────────────────────────────
  const handleApprove = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setLoadingActionId(id);
    try {
      await demandsAPI.updateStatus(id, 'EM_ANALISE', 'Triagem aprovada pela Central de Triagem.');
      fetchStats();
      onRefresh();
      if (viewerDemand?.id === id) setViewerDemand(null);
    } catch (_) { showAlert('error', 'Erro', 'Erro ao aprovar demanda.'); }
    finally { setLoadingActionId(null); }
  };

  const handleArchive = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setLoadingActionId(id);
    try {
      await demandsAPI.updateStatus(id, 'ARQUIVADA', 'Demanda arquivada pela Central de Triagem.');
      fetchStats();
      onRefresh();
      if (viewerDemand?.id === id) setViewerDemand(null);
    } catch (_) { showAlert('error', 'Erro', 'Erro ao arquivar demanda.'); }
    finally { setLoadingActionId(null); }
  };

  const handleToggleFavorite = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await demandsAPI.toggleFavorite(id);
      onRefresh();
    } catch (_) {}
  };

  // ── Filtragem ────────────────────────────────────────────
  let visible = demands.filter(d => d.status === 'RECEBIDA');

  if (filterSource !== 'all')   visible = visible.filter(d => d.source === filterSource);
  if (filterFavorite)           visible = visible.filter(d => d.isFavorite);

  if (globalSearch.trim()) {
    const s = globalSearch.toLowerCase();
    visible = visible.filter(d =>
      d.protocol.toLowerCase().includes(s) ||
      d.title.toLowerCase().includes(s) ||
      d.solicitanteName.toLowerCase().includes(s) ||
      d.city.toLowerCase().includes(s) ||
      d.themeArea.toLowerCase().includes(s)
    );
  }

  const totalPages = Math.ceil(visible.length / ITEMS_PER_PAGE);
  const paginatedVisible = visible.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full space-y-5 text-left">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Central de Triagem</h1>
            {/* Badge AO VIVO */}
            <button
              onClick={() => setIsLive(!isLive)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                isLive
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-300/50'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border border-slate-200/50'
              }`}
              title={isLive ? 'Atualização automática ativa — clique para pausar' : 'Pausado — clique para retomar'}
            >
              <Radio className={`w-2.5 h-2.5 ${isLive ? 'animate-pulse text-emerald-500' : 'text-slate-400'}`} />
              {isLive ? 'AO VIVO' : 'PAUSADO'}
            </button>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Demandas recebidas aguardando triagem e aprovação
          </p>
        </div>

        <button
          onClick={() => { fetchStats(); onRefresh(); }}
          className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-200 dark:hover:bg-slate-800 text-xs rounded-xl transition-all font-semibold"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {/* ── Cards de Indicadores ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            label: 'Aguardando Triagem',
            value: stats.recebidas,
            icon: <Activity className="w-5 h-5 text-blue-500" />,
            badge: 'bg-blue-50 dark:bg-blue-950/40',
            num: 'text-blue-700 dark:text-blue-400',
            border: 'border-blue-200/50 dark:border-blue-900/30'
          },
          {
            label: 'Em Análise',
            value: stats.processando,
            icon: <Loader2 className="w-5 h-5 text-amber-500" />,
            badge: 'bg-amber-50 dark:bg-amber-950/30',
            num: 'text-amber-700 dark:amber-400',
            border: 'border-amber-200/50 dark:border-amber-900/30'
          },
          {
            label: 'Revisão Obrigatória',
            value: stats.revisaoObrigatoria,
            icon: <TriangleAlert className="w-5 h-5 text-red-500" />,
            badge: 'bg-red-50 dark:bg-red-950/30',
            num: 'text-red-700 dark:text-red-400',
            border: 'border-red-200/50 dark:border-red-900/30'
          },
          {
            label: 'Resolvidas Hoje',
            value: stats.resolvidasHoje,
            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
            badge: 'bg-emerald-50 dark:bg-emerald-950/30',
            num: 'text-emerald-700 dark:text-emerald-400',
            border: 'border-emerald-200/50 dark:border-emerald-900/30'
          },
        ].map(card => (
          <div key={card.label} className={`glass-panel p-4 rounded-2xl border ${card.border} flex items-center gap-3`}>
            <div className={`p-2 rounded-xl ${card.badge}`}>{card.icon}</div>
            <div>
              <div className={`text-2xl font-black tabular-nums ${card.num}`}>{card.value}</div>
              <div className="text-[10px] text-slate-500 font-medium leading-tight">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filtros ── */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'WHATSAPP', 'EMAIL', 'MANUAL'] as const).map(src => (
          <button
            key={src}
            onClick={() => setFilterSource(src)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterSource === src
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            {src === 'all' ? 'Todas as Origens' : src === 'WHATSAPP' ? '💬 WhatsApp' : src === 'EMAIL' ? '📧 E-mail' : '✏️ Manual'}
          </button>
        ))}
        <button
          onClick={() => setFilterFavorite(!filterFavorite)}
          className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-xl text-xs font-semibold transition-all ${
            filterFavorite
              ? 'bg-amber-500/10 border-amber-400 text-amber-600'
              : 'bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'
          }`}
        >
          <Star className={`w-3 h-3 ${filterFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
          Favoritas
        </button>

        <span className="ml-auto text-xs text-slate-400 font-medium">
          {visible.length} demanda{visible.length !== 1 ? 's' : ''} aguardando triagem
        </span>
      </div>

      {/* ── Mobile: Compact Header Bar (hidden on md+) ── */}
      <div className="flex md:hidden items-center gap-2 px-1">
        <span className="text-xs font-semibold text-slate-500 shrink-0">
          {visible.length} demanda{visible.length !== 1 ? 's' : ''}
        </span>
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="search"
            readOnly
            placeholder="Buscar..."
            value={globalSearch}
            className="w-full pl-8 pr-3 py-2 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none"
          />
        </div>
        <button
          onClick={() => { fetchStats(); onRefresh(); }}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 transition-all"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* ── Mobile: Card List (hidden on md+) ── */}
      <div className="md:hidden space-y-3 overflow-y-auto flex-1">
        {paginatedVisible.length === 0 ? (
          <div className="glass-panel p-10 rounded-3xl text-center flex flex-col items-center gap-4 border border-slate-200 dark:border-slate-800">
            <div className="p-4 bg-emerald-500/10 rounded-full">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <div>
              <h3 className="font-bold text-base">Nenhuma demanda aguardando triagem</h3>
              <p className="text-slate-400 text-sm max-w-xs mt-1">
                {filterSource !== 'all' || filterFavorite
                  ? 'Nenhuma demanda encontrada com os filtros selecionados.'
                  : 'Novas demandas aparecerão aqui automaticamente.'}
              </p>
            </div>
          </div>
        ) : (
          paginatedVisible.map(demand => (
            <MobileDemandCard
              key={demand.id}
              demand={demand}
              onView={onSelectDemand}
              onEdit={onSelectDemandEdit}
              canEdit={userPermissions.includes('demand:edit')}
            />
          ))
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="min-h-[44px] flex-1 flex items-center justify-center gap-1 text-xs font-semibold bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 disabled:opacity-40"
            >
              ← Anterior
            </button>
            <span className="text-xs text-slate-400 shrink-0">{currentPage}/{totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="min-h-[44px] flex-1 flex items-center justify-center gap-1 text-xs font-semibold bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 disabled:opacity-40"
            >
              Próximo →
            </button>
          </div>
        )}
      </div>

      {/* ── Desktop: Lista de Demandas (100% largura) — hidden on mobile ── */}
      <div className="hidden md:block space-y-3 overflow-y-auto flex-1">
        {paginatedVisible.length === 0 ? (
          <div className="glass-panel p-14 rounded-3xl text-center flex flex-col items-center gap-4 border border-slate-200 dark:border-slate-800">
            <div className="p-4 bg-emerald-500/10 rounded-full">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Nenhuma demanda aguardando triagem</h3>
              <p className="text-slate-400 text-sm max-w-sm mt-1">
                {filterSource !== 'all' || filterFavorite
                  ? 'Nenhuma demanda encontrada com os filtros selecionados.'
                  : 'Novas demandas recebidas via WhatsApp ou e-mail aparecerão aqui automaticamente.'}
              </p>
            </div>
          </div>
        ) : (
          paginatedVisible.map(demand => {
            const isNew       = newDemandIds.has(demand.id);
            const hasAttachment = demand.attachments && demand.attachments.length > 0;

            return (
              <div
                key={demand.id}
                onClick={() => onSelectDemand(demand.id)}
                className={`glass-panel p-4 rounded-2xl border transition-all cursor-pointer relative overflow-hidden group ${
                  isNew
                    ? 'border-amber-400/70 shadow-lg shadow-amber-500/10 bg-amber-50/30 dark:bg-amber-950/10'
                    : 'border-slate-200/80 dark:border-slate-800/80 hover:border-blue-500/40 hover:shadow-md'
                }`}
              >
                {/* Barra lateral de confiança */}
                <div
                  className="absolute top-0 left-0 bottom-0 w-1 rounded-l-2xl"
                  style={{
                    backgroundColor: demand.confidenceScore >= 85 ? '#10b981'
                      : demand.confidenceScore >= 70 ? '#f59e0b' : '#ef4444'
                  }}
                />

                <div className="pl-3">
                  {/* Linha 1: Protocol + Badges */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {isNew && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-amber-400 text-amber-900 animate-pulse">
                        ● NOVA
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400 font-mono font-bold bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded">
                      {demand.protocol}
                    </span>
                    <SourceBadge source={demand.source} />
                    <PriorityBadge priority={demand.priority} />
                    <span className="text-[10px] text-slate-400 flex items-center gap-1 ml-auto">
                      <Clock className="w-3 h-3" />
                      {new Date(demand.receivedAt).toLocaleDateString('pt-BR')}{' '}
                      {new Date(demand.receivedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  {/* Linha 2: Título */}
                  <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 leading-tight mb-1">
                    {demand.title}
                  </h3>

                  {/* Linha 3: Resumo */}
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3 leading-relaxed">
                    {demand.summary}
                  </p>

                  {/* Linha 4: Metadados */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-[11px] text-slate-500">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <User className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="truncate font-semibold text-slate-700 dark:text-slate-300">{demand.solicitanteName}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="font-semibold text-slate-700 dark:text-slate-300">{demand.city} - {demand.uf}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-indigo-400 shrink-0" />
                      <span className="font-semibold text-slate-700 dark:text-slate-300">{demand.themeArea}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">
                        {demand.contact?.phone || '—'}
                      </span>
                    </div>
                  </div>

                  {/* Linha 5: Score + Arquivo + Ações */}
                  <div className="flex items-center justify-between gap-3 pt-2.5 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-4">
                      <ConfidenceBar score={demand.confidenceScore} />
                      {hasAttachment && (
                        <span className="flex items-center gap-1 text-[10px] text-slate-400">
                          <FileText className="w-3 h-3" />
                          {demand.attachments![0].fileName}
                        </span>
                      )}
                    </div>

                    {/* Ações — visíveis no hover */}
                    <div
                      className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={e => e.stopPropagation()}
                    >
                      {/* Visualizar Documento */}
                      <button
                        onClick={e => { e.stopPropagation(); setViewerDemand(demand); }}
                        className="p-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-950/50 border border-blue-200/30 rounded-xl text-blue-700 dark:text-blue-400 transition-all"
                        title="Visualizar documento e dados da IA"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>

                      {/* Favoritar */}
                      <button
                        onClick={e => handleToggleFavorite(demand.id, e)}
                        className="p-2 bg-slate-100 hover:bg-amber-50 dark:bg-slate-900 border border-slate-200/50 rounded-xl text-slate-400 hover:text-amber-500 transition-all"
                        title="Favoritar"
                      >
                        <Star className={`w-3.5 h-3.5 ${demand.isFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
                      </button>

                      {userPermissions.includes('demand:edit') && (
                        <>
                          {/* Editar */}
                          <button
                            onClick={e => { e.stopPropagation(); onSelectDemandEdit(demand.id); }}
                            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 border border-slate-200/50 rounded-xl text-slate-500 transition-all"
                            title="Editar demanda"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>

                          {/* Aprovar */}
                          <button
                            onClick={e => handleApprove(demand.id, e)}
                            disabled={loadingActionId === demand.id}
                            className="flex items-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white rounded-xl text-[11px] font-bold transition-all shadow shadow-emerald-500/20"
                            title="Aprovar — mover para Em Análise"
                          >
                            {loadingActionId === demand.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Check className="w-3.5 h-3.5" />
                            }
                            Aprovar
                          </button>

                          {/* Arquivar */}
                          <button
                            onClick={e => handleArchive(demand.id, e)}
                            disabled={loadingActionId === demand.id}
                            className="p-2 bg-slate-100 hover:bg-red-50 dark:bg-slate-900 border border-slate-200/50 rounded-xl text-slate-400 hover:text-red-500 transition-all"
                            title="Arquivar demanda"
                          >
                            <Archive className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-[#1a1c23] border border-slate-200 dark:border-slate-800/60 sm:px-6 mt-4 rounded-xl shadow-sm">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Mostrando <span className="font-medium text-blue-600 dark:text-blue-400">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> a <span className="font-medium text-blue-600 dark:text-blue-400">{Math.min(currentPage * ITEMS_PER_PAGE, visible.length)}</span> de <span className="font-medium text-blue-600 dark:text-blue-400">{visible.length}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#1f222b] text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Anterior</span>
                    &larr;
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#1f222b] text-sm font-medium text-slate-700 dark:text-slate-300">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#1f222b] text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Próximo</span>
                    &rarr;
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal: Visualizador de Documento ── */}
      {viewerDemand && (
        <DocumentViewer
          demand={viewerDemand}
          onClose={() => setViewerDemand(null)}
          onApprove={() => handleApprove(viewerDemand.id)}
          onArchive={() => handleArchive(viewerDemand.id)}
          onOpenFull={() => { onSelectDemand(viewerDemand.id); setViewerDemand(null); }}
          loading={loadingActionId === viewerDemand.id}
        />
      )}
    </div>
  );
};

export default Inbox;
